/**
 * Smart Waste App - Main entry with theme + auth providers and routing
 */
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';

import PrivateRoute from './components/Common/PrivateRoute';
import RoleRoute from './components/Common/RoleRoute';
import Layout from './components/Common/Layout';

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress color="primary" />
  </Box>
);

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const RegisterCollector = lazy(() => import('./pages/RegisterCollector'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const UpdatePassword = lazy(() => import('./pages/UpdatePassword'));
const Setup = lazy(() => import('./pages/Setup'));
const PendingApproval = lazy(() => import('./pages/PendingApproval'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ResidentPortal = lazy(() => import('./pages/ResidentPortal'));
const BinStationDisposal = lazy(() => import('./pages/BinStationDisposal'));
const ResidentManagement = lazy(() => import('./pages/ResidentManagement'));
const WasteCategories = lazy(() => import('./pages/WasteCategories'));
const Estates = lazy(() => import('./pages/Estates'));
const BinStations = lazy(() => import('./pages/BinStations'));
const CollectionSchedule = lazy(() => import('./pages/CollectionSchedule'));
const BillingManagement = lazy(() => import('./pages/BillingManagement'));
const CollectorManagement = lazy(() => import('./pages/CollectorManagement'));
const CollectorDashboard = lazy(() => import('./components/Dashboard/CollectorDashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const Reports = lazy(() => import('./pages/Reports'));
const Support = lazy(() => import('./pages/Support'));
const Landing = lazy(() => import('./pages/Landing'));

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SocketProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/register/collector" element={<RegisterCollector />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/update-password" element={<UpdatePassword />} />
                <Route path="/setup" element={<Setup />} />
                <Route path="/pending-approval" element={<PendingApproval />} />

                <Route
                  path="/*"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/portal" element={<RoleRoute allowedRoles={['resident']}><ResidentPortal /></RoleRoute>} />
                          <Route path="/resident-portal" element={<Navigate to="/portal" replace />} />
                          <Route path="/dispose" element={<RoleRoute allowedRoles={['resident']}><BinStationDisposal /></RoleRoute>} />
                          <Route path="/collector" element={<RoleRoute allowedRoles={['collector']}><CollectorDashboard /></RoleRoute>} />
                          <Route path="/collector-dashboard" element={<Navigate to="/collector" replace />} />
                          <Route path="/residents" element={<RoleRoute allowedRoles={['super_admin']}><ResidentManagement /></RoleRoute>} />
                          <Route path="/estates" element={<RoleRoute allowedRoles={['super_admin']}><Estates /></RoleRoute>} />
                          <Route path="/categories" element={<RoleRoute allowedRoles={['super_admin']}><WasteCategories /></RoleRoute>} />
                          <Route path="/stations" element={<RoleRoute allowedRoles={['super_admin']}><BinStations /></RoleRoute>} />
                          <Route path="/schedule" element={<RoleRoute allowedRoles={['super_admin']}><CollectionSchedule /></RoleRoute>} />
                          <Route path="/billing" element={<RoleRoute allowedRoles={['super_admin']}><BillingManagement /></RoleRoute>} />
                          <Route path="/collectors" element={<RoleRoute allowedRoles={['super_admin']}><CollectorManagement /></RoleRoute>} />
                          <Route path="/reports" element={<RoleRoute allowedRoles={['super_admin', 'resident']}><Reports /></RoleRoute>} />
                          <Route path="/support" element={<Support />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/notifications" element={<NotificationsPage />} />
                          <Route path="/help-center" element={<HelpCenter />} />
                        </Routes>
                      </Layout>
                    </PrivateRoute>
                  }
                />
              </Routes>
            </Suspense>
          </Router>
        </SocketProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
